import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { API_BASE } from './apiConfig';
import { createLiveStore } from './live/liveStore';
import { useToast } from './components/ui/Toast';

/**
 * SocketContext — one Socket.IO connection to the enableportal relay, and live state for
 * every session the user may see, keyed by sessionId.
 *
 * Relay contract (enableportal server/sockets/session.js, feature/relay-floor):
 *   portal → server   session:list (ack: [{sessionId, game, userId, startTime}]), polled every 3 s
 *                     session:join {sessionId} / session:leave {sessionId}
 *                     session:control {sessionId, command: 'pause'|'unpause'|'stop'}
 *                     session:param {sessionId, paramName, paramValue, phase}
 *   server → portal   session:skeleton | session:suki | session:event (volatile, from the game)
 *                     session:status {sessionId, status: active|playing|paused|ended|disconnected}
 *                     relay:error {event, error}   — refusals, which carry no sessionId
 *
 * Rooms are joined only while something watches a session (useLiveSession), so the Live
 * overview and the sidebar never stream skeleton data for every patient.
 */
const SocketContext = createContext(null);
export const LIST_POLL_MS = 3000;

const RELAY_COPY = {
    'session:join': 'You don’t have access to this live session.',
    'session:start': 'You don’t have access to this live session.',
    'session:control': 'The relay refused the game control — the session wasn’t joined yet.',
    'session:param': 'The relay refused the parameter change — the session wasn’t joined yet.',
    'session:list': 'The relay refused the session list for this account.',
};

export function relayErrorText(err) {
    if (!err) return '';
    return RELAY_COPY[err.event] || err.error || 'The relay refused the request.';
}

export function SocketProvider({ children }) {
    const { isAuthenticated, user } = useAuth();
    const toast = useToast();
    const storeRef = useRef(null);
    if (!storeRef.current) storeRef.current = createLiveStore();
    const store = storeRef.current;

    const socketRef = useRef(null);
    const watchCount = useRef(new Map()); // sessionId -> refcount
    const pendingJoins = useRef(new Map()); // sessionId -> emitted at
    const [connected, setConnected] = useState(false);
    const [connectError, setConnectError] = useState(null);

    const userId = user?.user_id;

    // Connection
    useEffect(() => {
        if (!isAuthenticated) return undefined;
        const token = localStorage.getItem('enablegames_auth_token');
        const socket = io(API_BASE, {
            auth: { token },
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 8000,
        });
        socketRef.current = socket;

        const join = (sid) => {
            pendingJoins.current.set(sid, Date.now());
            socket.emit('session:join', { sessionId: sid });
        };
        socket.on('connect', () => {
            setConnected(true);
            setConnectError(null);
            // Rejoin every watched room after a reconnect.
            watchCount.current.forEach((n, sid) => { if (n > 0) join(sid); });
        });
        socket.on('disconnect', () => setConnected(false));
        socket.on('connect_error', (err) => setConnectError(err?.message || 'connect_error'));

        const sidOf = (data) => {
            if (data && data.sessionId) return data.sessionId;
            // Payloads without a sessionId can only be attributed when one room is joined.
            const watched = [...watchCount.current.entries()].filter(([, n]) => n > 0);
            return watched.length === 1 ? watched[0][0] : null;
        };
        const parse = (d) => {
            if (typeof d !== 'string') return d;
            try { return JSON.parse(d); } catch { return null; }
        };
        socket.on('session:skeleton', (d) => { const x = parse(d); const sid = sidOf(x); if (sid) store.onSkeleton(sid, x); });
        socket.on('session:suki', (d) => { const x = parse(d); const sid = sidOf(x); if (sid) store.onSuki(sid, x); });
        socket.on('session:event', (d) => { const x = parse(d); const sid = sidOf(x); if (sid) store.onEvent(sid, x); });
        socket.on('session:status', (d) => {
            const x = parse(d);
            const sid = sidOf(x);
            if (!sid) return;
            pendingJoins.current.delete(sid);
            store.onStatus(sid, x);
        });
        // Fallback for transports without acks: the server may push the list as an event.
        socket.on('session:list', (arr) => store.setList(arr));

        socket.on('relay:error', (raw) => {
            const err = parse(raw) || {};
            store.addError(err);
            if (err.event === 'session:join' || err.event === 'session:start') {
                // relay:error has no sessionId — attribute it to the most recent pending join.
                const pending = [...pendingJoins.current.entries()].sort((a, b) => b[1] - a[1])[0];
                if (pending) {
                    store.setJoinError(pending[0], relayErrorText(err));
                    pendingJoins.current.delete(pending[0]);
                }
            }
            toast(relayErrorText(err), 'error');
        });

        return () => {
            socket.removeAllListeners();
            socket.disconnect();
            socketRef.current = null;
            setConnected(false);
            store.clearAll();
        };
    }, [isAuthenticated, userId, store, toast]);

    // session:list polling
    useEffect(() => {
        const socket = socketRef.current;
        if (!socket || !connected) return undefined;
        let alive = true;
        const poll = () => {
            const timer = setTimeout(() => { if (alive) store.setList(null, 'timeout'); }, 8000);
            socket.emit('session:list', (sessions) => {
                clearTimeout(timer);
                if (alive) store.setList(sessions);
            });
        };
        poll();
        const id = setInterval(poll, LIST_POLL_MS);
        return () => { alive = false; clearInterval(id); };
    }, [connected, store]);

    const watch = useCallback((sid) => {
        if (!sid) return () => {};
        const m = watchCount.current;
        const n = (m.get(sid) || 0) + 1;
        m.set(sid, n);
        const socket = socketRef.current;
        if (n === 1 && socket?.connected) {
            pendingJoins.current.set(sid, Date.now());
            store.setJoinError(sid, null);
            socket.emit('session:join', { sessionId: sid });
        }
        return () => {
            const left = (m.get(sid) || 1) - 1;
            if (left > 0) { m.set(sid, left); return; }
            m.delete(sid);
            pendingJoins.current.delete(sid);
            // A disconnected socket has already lost its rooms; don't buffer a stale leave.
            if (socketRef.current?.connected) socketRef.current.emit('session:leave', { sessionId: sid });
        };
    }, [store]);

    const sendControl = useCallback((sid, command) => {
        const socket = socketRef.current;
        if (!socket?.connected || !sid) return false;
        socket.emit('session:control', { sessionId: sid, command, ts: Date.now() });
        return true;
    }, []);

    const sendParam = useCallback((sid, paramName, paramValue, phase = 'warmup') => {
        const socket = socketRef.current;
        if (!socket?.connected || !sid) return false;
        socket.emit('session:param', { sessionId: sid, paramName, paramValue: String(paramValue), phase });
        return true;
    }, []);

    const reconnect = useCallback(() => {
        const s = socketRef.current;
        if (s && !s.connected) s.connect();
    }, []);

    const value = useMemo(() => ({ store, connected, connectError, watch, sendControl, sendParam, reconnect }), [store, connected, connectError, watch, sendControl, sendParam, reconnect]);
    return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}

const NOOP_CTX = {
    store: null, connected: false, connectError: null, watch: () => () => {}, sendControl: () => false, sendParam: () => false, reconnect: () => {},
};

export function useSocket() {
    return useContext(SocketContext) || NOOP_CTX;
}

const EMPTY_LIST = { sessions: [], loaded: false, error: null, at: 0 };
const noSub = () => () => {};

/** session:list — every live session the user may see. */
export function useLiveList() {
    const { store } = useSocket();
    return useSyncExternalStore(
        store ? (fn) => store.subscribe('list', fn) : noSub,
        store ? store.getList : () => EMPTY_LIST,
    );
}

/**
 * Live state of one session. Joins its room while mounted (watch: false to read only).
 * Returns null until anything has arrived for that session.
 */
export function useLiveSessionState(sessionId, { watch: doWatch = true } = {}) {
    const { store, watch, connected } = useSocket();
    useEffect(() => {
        if (!doWatch || !sessionId || !connected) return undefined;
        return watch(sessionId);
    }, [doWatch, sessionId, connected, watch]);
    return useSyncExternalStore(
        store && sessionId ? (fn) => store.subscribe(sessionId, fn) : noSub,
        () => (store && sessionId ? store.get(sessionId) : null),
    );
}

/** The signed-in player's own live session, if one is listed. */
export function useOwnLiveSession() {
    const { user } = useAuth();
    const list = useLiveList();
    return useMemo(() => list.sessions.find((s) => user && s.userId === user.user_id) || null, [list, user]);
}
